const PREFS_KEY='smco.notification.preferences.v1';
const STATE_KEY_PREFIX='smco.notification.state.v1.';

export const DEFAULT_NOTIFICATION_PREFERENCES={
  enabled:false,
  email:true,
  cases:true,
  tasks:true,
};

function safeJson(value,fallback){
  try{return JSON.parse(value)}catch{return fallback}
}

export function getNotificationPreferences(){
  if(typeof window==='undefined')return {...DEFAULT_NOTIFICATION_PREFERENCES};
  return {...DEFAULT_NOTIFICATION_PREFERENCES,...safeJson(window.localStorage.getItem(PREFS_KEY)||'',{})};
}

export function setNotificationPreferences(next){
  const value={...getNotificationPreferences(),...(next||{})};
  if(typeof window!=='undefined'){
    window.localStorage.setItem(PREFS_KEY,JSON.stringify(value));
    window.dispatchEvent(new CustomEvent('crm-notification-preferences-change',{detail:value}));
  }
  return value;
}

function staffKey(staffId){
  return STATE_KEY_PREFIX+String(staffId||'staff').replace(/[^a-zA-Z0-9._-]/g,'_');
}

export function getNotificationState(staffId){
  if(typeof window==='undefined')return {initialised:false,seen:[],read:[]};
  const stored=safeJson(window.localStorage.getItem(staffKey(staffId))||'',{});
  return {
    initialised:Boolean(stored.initialised),
    seen:Array.isArray(stored.seen)?stored.seen:[],
    read:Array.isArray(stored.read)?stored.read:[],
  };
}

export function setNotificationState(staffId,state){
  if(typeof window==='undefined')return state;
  const next={
    initialised:Boolean(state?.initialised),
    seen:[...new Set(Array.isArray(state?.seen)?state.seen:[])].slice(-600),
    read:[...new Set(Array.isArray(state?.read)?state.read:[])].slice(-600),
  };
  window.localStorage.setItem(staffKey(staffId),JSON.stringify(next));
  return next;
}

function tauriNotificationApi(){
  if(typeof window==='undefined')return null;
  const api=window.__TAURI__?.notification;
  return api&&typeof api.sendNotification==='function'?api:null;
}

export function notificationRuntime(){
  if(tauriNotificationApi())return 'tauri';
  if(typeof window!=='undefined'&&'Notification' in window)return 'browser';
  return 'unsupported';
}

export async function getSystemNotificationPermission(){
  const tauri=tauriNotificationApi();
  if(tauri){
    try{return await tauri.isPermissionGranted()?'granted':'prompt'}catch{return 'prompt'}
  }
  if(typeof window!=='undefined'&&'Notification' in window)return normalisePermission(window.Notification.permission);
  return 'unsupported';
}

async function withTimeout(promise, timeoutMs, fallback){
  let timer;
  try{
    return await Promise.race([
      promise,
      new Promise(resolve=>{timer=window.setTimeout(()=>resolve(fallback),timeoutMs)}),
    ]);
  }finally{
    if(timer)window.clearTimeout(timer);
  }
}

function normalisePermission(value){
  const permission=String(value||'').toLowerCase();
  if(permission==='granted')return 'granted';
  if(permission==='denied')return 'denied';
  if(permission==='default'||permission==='prompt')return 'prompt';
  if(permission==='timeout'||permission==='blocked')return 'blocked';
  return permission||'prompt';
}

export async function requestSystemNotificationPermission(){
  const tauri=tauriNotificationApi();
  if(tauri){
    try{
      if(await withTimeout(Promise.resolve(tauri.isPermissionGranted()),5000,false))return 'granted';
      const result=await withTimeout(Promise.resolve(tauri.requestPermission()),10000,'blocked');
      return normalisePermission(result);
    }catch{return 'denied'}
  }

  if(typeof window!=='undefined'&&'Notification' in window){
    const current=normalisePermission(window.Notification.permission);
    if(current==='granted'||current==='denied')return current;

    try{
      const request=Promise.resolve(window.Notification.requestPermission());
      const result=await withTimeout(request,10000,'blocked');
      return normalisePermission(result);
    }catch{
      return 'denied';
    }
  }

  return 'unsupported';
}

export async function sendSystemNotification({title,body,tag,route}){
  const tauri=tauriNotificationApi();
  if(tauri){
    const granted=await tauri.isPermissionGranted().catch(()=>false);
    if(!granted)return false;
    tauri.sendNotification({
      title:String(title||'Sousa Murray Customer Operations').slice(0,120),
      body:String(body||'').slice(0,260),
    });
    return true;
  }
  if(typeof window!=='undefined'&&'Notification' in window&&window.Notification.permission==='granted'){
    const notification=new window.Notification(String(title||'Sousa Murray Customer Operations').slice(0,120),{
      body:String(body||'').slice(0,260),
      tag:String(tag||'smco').slice(0,120),
      renotify:false,
    });
    notification.onclick=()=>{
      try{window.focus()}catch{}
      if(route)window.location.assign(route);
      notification.close();
    };
    return true;
  }
  return false;
}

export async function sendTestSystemNotification(){
  return sendSystemNotification({
    title:'Sousa Murray Customer Operations',
    body:'Computer notifications are working correctly.',
    tag:'smco-notification-test',
    route:'/',
  });
}
